import axios from 'axios';
import { getApiUrl } from '../utils/config';
import { getAuthHeader } from './authService';

export interface User {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  profileImageUrl: string | null;
  gender: string | null;
  role: string;
  active: boolean;
  stravaAthleteId: number | null;
  hasStravaToken: boolean;
  createdAt: any;
  updatedAt: any;
}

interface ApiResponse<T> {
  result: string;
  message: string;
  data: T;
}

/**
 * Lấy danh sách tất cả người dùng
 * @returns Danh sách người dùng
 */
export const getAllUsers = async (): Promise<User[]> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn cần đăng nhập để xem danh sách người dùng');
    }

    const response = await axios.get<ApiResponse<User[]>>(
      `${getApiUrl()}/api/users`,
      {
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.data.result === 'SUCCESS') {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Không thể lấy danh sách người dùng');
    }
  } catch (error: any) {
    console.error('Lỗi khi lấy danh sách người dùng:', error);
    throw new Error(error.response?.data?.message || 'Không thể lấy danh sách người dùng');
  }
};

/**
 * Cấp quyền admin cho người dùng
 * @param userId ID người dùng cần cấp quyền
 * @returns Kết quả cấp quyền
 */
export const grantAdminRole = async (userId: number): Promise<{ success: boolean; message: string }> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn cần đăng nhập để thực hiện thao tác này');
    }

    const response = await axios.put<ApiResponse<any>>(
      `${getApiUrl()}/api/users/${userId}/grant-admin`,
      {},
      {
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.data.result === 'SUCCESS') {
      return { success: true, message: 'Đã cấp quyền admin thành công' };
    } else {
      throw new Error(response.data.message || 'Không thể cấp quyền admin');
    }
  } catch (error: any) {
    console.error('Lỗi khi cấp quyền admin:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Không thể cấp quyền admin' 
    };
  }
};

/**
 * Thu hồi quyền admin của người dùng
 * @param userId ID người dùng cần thu hồi quyền
 * @returns Kết quả thu hồi quyền
 */
export const revokeAdminRole = async (userId: number): Promise<{ success: boolean; message: string }> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn cần đăng nhập để thực hiện thao tác này');
    }

    const response = await axios.put<ApiResponse<any>>(
      `${getApiUrl()}/api/users/${userId}/revoke-admin`,
      {},
      {
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.data.result === 'SUCCESS') {
      return { success: true, message: 'Đã thu hồi quyền admin thành công' };
    } else {
      throw new Error(response.data.message || 'Không thể thu hồi quyền admin');
    }
  } catch (error: any) {
    console.error('Lỗi khi thu hồi quyền admin:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Không thể thu hồi quyền admin' 
    };
  }
};

/**
 * Vô hiệu hóa tài khoản người dùng
 * @param userId ID người dùng cần vô hiệu hóa
 * @returns Kết quả vô hiệu hóa tài khoản
 */
export const deactivateUser = async (userId: number): Promise<{ success: boolean; message: string }> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn cần đăng nhập để thực hiện thao tác này');
    }

    const response = await axios.put<ApiResponse<any>>(
      `${getApiUrl()}/api/users/${userId}/deactivate`,
      {},
      {
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.data.result === 'SUCCESS') {
      return { success: true, message: 'Đã vô hiệu hóa tài khoản thành công' };
    } else {
      throw new Error(response.data.message || 'Không thể vô hiệu hóa tài khoản');
    }
  } catch (error: any) {
    console.error('Lỗi khi vô hiệu hóa tài khoản:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Không thể vô hiệu hóa tài khoản' 
    };
  }
};

/**
 * Hủy kết nối Strava của người dùng
 * @param userId ID người dùng cần hủy kết nối Strava
 * @returns Kết quả hủy kết nối
 */
export const deauthorizeStravaToken = async (userId: number): Promise<{ success: boolean; message: string }> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn cần đăng nhập để thực hiện thao tác này');
    }

    const response = await axios.delete<ApiResponse<any>>(
      `${getApiUrl()}/api/strava/deauthorize/${userId}`,
      {
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.data.result === 'SUCCESS') {
      return { success: true, message: 'Đã hủy kết nối Strava thành công' };
    } else {
      throw new Error(response.data.message || 'Không thể hủy kết nối Strava');
    }
  } catch (error: any) {
    console.error('Lỗi khi hủy kết nối Strava:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Không thể hủy kết nối Strava' 
    };
  }
}; 